import { useMutation, useQueryClient } from '@tanstack/react-query';

import { markBackInStockWaitlistFulfillmentsViewed } from 'api/jalapeno';
import useAccountQuery from 'api/queries/useAccountQuery';
import { UNVIEWED_BACK_IN_STOCK_WAITLIST_FULFILLMENTS_QUERY_KEY } from 'api/queries/useUnviewedBackInStockWaitlistFulfillmentsQuery';
import logError from 'utils/errorUtils';

const useMarkBackInStockWaitlistFulfillmentsViewed = () => {
    const queryClient = useQueryClient();
    const { data: account } = useAccountQuery();
    const { chargeID } = account?.nextOrder ?? {};

    return useMutation({
        mutationFn: ({ chargeID: mutationChargeID = chargeID, requestIDs }) => {
            if (!mutationChargeID) {
                return Promise.reject(new Error('Missing chargeID for markBackInStockWaitlistFulfillmentsViewed'));
            }

            return markBackInStockWaitlistFulfillmentsViewed({
                chargeID: mutationChargeID,
                sendParams: { requestIDs },
            });
        },
        onMutate: ({ chargeID: mutationChargeID = chargeID, requestIDs }) => {
            const queryKey = [UNVIEWED_BACK_IN_STOCK_WAITLIST_FULFILLMENTS_QUERY_KEY, mutationChargeID];
            // Do not await: avoids a render gap where remounted shop/PDP layouts reopen the modal
            // from stale unviewed fulfillments still in the React Query cache.
            queryClient.cancelQueries({ queryKey }, { revert: false, silent: true });

            const previousData = queryClient.getQueryData(queryKey);
            const dismissedIDs = new Set(requestIDs ?? []);

            queryClient.setQueryData(queryKey, (old) => {
                if (!old || typeof old !== 'object') return old;

                const items = (old.items ?? []).filter(
                    (item) =>
                        item?.backInStockWaitlistRequestID == null ||
                        !dismissedIDs.has(item.backInStockWaitlistRequestID)
                );

                return {
                    ...old,
                    items,
                };
            });

            return { previousData, queryKey };
        },
        onError: (error, _variables, context) => {
            if (context?.previousData !== undefined && context?.queryKey) {
                queryClient.setQueryData(context.queryKey, context.previousData);
            }
            logError(error);
            console.error(error);
        },
        onSettled: (_data, _error, variables) => {
            const settledChargeID = variables?.chargeID ?? chargeID;
            queryClient.invalidateQueries({
                queryKey: [UNVIEWED_BACK_IN_STOCK_WAITLIST_FULFILLMENTS_QUERY_KEY, settledChargeID],
            });
        },
    });
};

export default useMarkBackInStockWaitlistFulfillmentsViewed;
